<template>
  <div class="phone-login">
    <h1 v-t.preserve="'auth.phone.title'" />
    <h2 v-t.preserve="'auth.phone.subtitle'" />

    <form class="input-container card card--rounded pt-5" @submit.prevent="onSubmit" novalidate autocomplete="off">
      <template v-if="!otpSent">
        <div ref="countryWrap" class="phone-field-wrap">
          <div
            class="phone-field"
            :class="{ 'phone-field--error': $v.nationalNumber.$error }"
          >
            <button
              type="button"
              class="phone-field__prefix"
              :aria-expanded="countryPickerOpen ? 'true' : 'false'"
              aria-haspopup="listbox"
              @click="toggleCountryPicker"
            >
              <span class="phone-field__dial">+{{ selectedCountry.dial }}</span>
              <span class="phone-field__caret" aria-hidden="true" />
            </button>
            <span class="phone-field__divider" aria-hidden="true" />
            <div class="phone-field__input-wrap">
              <input
                id="phone-national"
                :value="nationalNumber"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                autocomplete="tel-national"
                maxlength="15"
                :placeholder="$t('auth.phone.placeholder.nationalNumber')"
                :aria-invalid="$v.nationalNumber.$error"
                @keydown="onNationalNumberKeydown"
                @input="onNationalNumberInput"
                @paste="onNationalNumberPaste"
              />
            </div>
          </div>

          <div
            v-show="countryPickerOpen"
            class="country-dropdown"
            role="listbox"
            :aria-label="$t('auth.phone.countryPickerTitle')"
            @click.stop
          >
            <input
              ref="countrySearchInput"
              v-model="countrySearch"
              type="search"
              class="country-dropdown__search"
              :placeholder="$t('auth.phone.countrySearch')"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
            />
            <ul class="country-dropdown__list">
              <li
                v-for="(c, idx) in filteredCountries"
                :key="c.iso2 + '-' + c.dial + '-' + idx"
                class="country-dropdown__row"
                role="option"
                :aria-selected="c.iso2 === selectedCountry.iso2 && c.dial === selectedCountry.dial"
                @mousedown.prevent="selectCountry(c)"
              >
                <span class="country-dropdown__flag" aria-hidden="true">{{ flagEmoji(c.iso2) }}</span>
                <span class="country-dropdown__name">{{ c.name }}</span>
                <span class="country-dropdown__code">(+{{ c.dial }})</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="info">
          <error-text :active="!!errorCode || !!errorDetail || !!$v.$error">
            <span v-if="errorDetail">{{ errorDetail }}</span>
            <template v-else>
              <span v-if="errorCode === 400" v-t.preserve="'auth.phone.error.invalidRequest'" />
              <span v-else-if="errorCode === 404" v-t.preserve="'auth.phone.error.notFound'" />
              <span v-else-if="!!errorCode" v-t.preserve="'auth.phone.error.generic'" />
              <span v-else-if="$v.nationalNumber.$error" v-t.preserve="'auth.phone.error.nationalNumber'" />
            </template>
          </error-text>
        </div>
        <btn type="button" flat :loading="loading" @click="sendPin">{{ $t('auth.phone.continuePhone') }}</btn>
      </template>

      <template v-else>
        <p class="otp-hint" v-t.preserve="'auth.phone.otpHint'" />
        <pk-input v-model="pin" :placeholder="$t('auth.phone.placeholder.pin')" type="text" inputmode="numeric" :maxlength="8" :error="$v.pin.$error" />
        <div class="info">
          <error-text :active="!!errorCode || !!errorDetail || !!$v.$error">
            <span v-if="errorDetail">{{ errorDetail }}</span>
            <template v-else>
              <span v-if="errorCode === 400" v-t.preserve="'auth.phone.error.invalidRequest'" />
              <span v-else-if="!!errorCode" v-t.preserve="'auth.phone.error.generic'" />
              <span v-else-if="$v.pin.$error" v-t.preserve="'auth.phone.error.pin'" />
            </template>
          </error-text>
        </div>
        <btn flat :loading="loading" submit>{{ $t('auth.phone.signIn') }}</btn>
        <a class="change-number" href="#" @click.prevent="resetOtp">{{ $t('auth.phone.changeNumber') }}</a>
      </template>

      <a class="back-email" href="#" @click.prevent="backToEmail">{{ $t('auth.phone.backToEmail') }}</a>
    </form>
  </div>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators'
import { AuthenticationPlatform } from '@/api/peanutbutter'
import sabAPI from '@/api/sab'
import authenticationService from '@/js/AuthenticationService'
import formSubmit from '@/js/mixins/formSubmit'
import { mapState } from 'vuex'
import { getIpGeoSnapshot } from '@/js/mtnGhanaGeo'
import COUNTRY_CALLING_CODES from '@/data/countryCallingCodes.json'
import { messageFromApiError } from '@/js/messageFromApiError'

/** First calling-code row for an ISO 3166-1 alpha-2 country (matches `window.countryCode` from IP). */
function countryEntryForIpIso(iso2) {
  const iso = String(iso2 || '')
    .trim()
    .toUpperCase()
  if (!iso || iso.length !== 2) {
    return null
  }
  const matches = COUNTRY_CALLING_CODES.filter(c => c.iso2 === iso)
  if (!matches.length) {
    return null
  }
  if (matches.length === 1) {
    return matches[0]
  }
  return matches.reduce((a, b) =>
    String(a.dial).length <= String(b.dial).length ? a : b
  )
}

function fallbackCountryEntry() {
  return (
    countryEntryForIpIso('US') ||
    COUNTRY_CALLING_CODES[0]
  )
}

/** Normalize GeoJS / SAB country to ISO alpha-2. */
function normalizeGeoCountryCode(raw) {
  const s = String(raw || '')
    .trim()
    .toUpperCase()
  if (s.length >= 2) {
    return s.slice(0, 2)
  }
  return ''
}

/**
 * Picks ISO2 using SAB `/utils/country` and GeoJS `getIpGeoSnapshot` (both IP-based).
 * When both agree, that wins; else prefers SAB if listed; else Geo if listed; else store fallback.
 */
function resolveCountryIsoFromIpHints(sabIsoRaw, geoSnapshot, storeFallbackIso) {
  const sabIso = normalizeGeoCountryCode(sabIsoRaw)
  const geoIso = geoSnapshot && geoSnapshot.countryCode ? normalizeGeoCountryCode(geoSnapshot.countryCode) : ''
  const sabEntry = sabIso ? countryEntryForIpIso(sabIso) : null
  const geoEntry = geoIso ? countryEntryForIpIso(geoIso) : null

  if (sabEntry && geoEntry && sabIso === geoIso) {
    return sabIso
  }

  if (geoEntry) {
    return geoIso
  }

  if (sabEntry) {
    return sabIso
  }

  return normalizeGeoCountryCode(storeFallbackIso) || 'US'
}

function flagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) {
    return '🏳️'
  }
  const A = 0x1f1e6
  const c = iso2.toUpperCase()
  return String.fromCodePoint(A + c.charCodeAt(0) - 65) + String.fromCodePoint(A + c.charCodeAt(1) - 65)
}

export default {
  name: 'phone-login',
  mixins: [formSubmit('verifyLogin')],
  data() {
    const fromStore = countryEntryForIpIso(this.$store.state.window.countryCode)
    return {
      otpSent: false,
      selectedCountry: { ...(fromStore || fallbackCountryEntry()) },
      nationalNumber: '',
      pin: '',
      countryPickerOpen: false,
      countrySearch: '',
      errorDetail: null,
      phoneCountryUserChosen: false
    }
  },
  validations: {
    nationalNumber: { required, minLength: minLength(8) },
    pin: { required, minLength: minLength(4) }
  },
  computed: {
    ...mapState({
      windowCountryCode: state => state.window.countryCode
    }),
    filteredCountries() {
      const q = (this.countrySearch || '').trim().toLowerCase()
      if (!q) {
        return COUNTRY_CALLING_CODES
      }
      return COUNTRY_CALLING_CODES.filter(c => {
        const name = (c.name || '').toLowerCase()
        const dial = String(c.dial || '')
        return name.includes(q) || dial.includes(q) || `+${dial}`.includes(q)
      })
    }
  },
  watch: {
    windowCountryCode(iso) {
      if (this.phoneCountryUserChosen || this.otpSent) {
        return
      }
      this.applyCountryFromIso(iso)
    },
    countryPickerOpen(open) {
      if (open) {
        this.countrySearch = ''
        document.addEventListener('keydown', this.onCountryPickerKeydown)
        document.addEventListener('click', this.onDocumentClickClosePicker)
        this.$nextTick(() => {
          const el = this.$refs.countrySearchInput
          if (el && typeof el.focus === 'function') {
            el.focus()
          }
        })
      } else {
        document.removeEventListener('keydown', this.onCountryPickerKeydown)
        document.removeEventListener('click', this.onDocumentClickClosePicker)
      }
    }
  },
  async mounted() {
    if (this.phoneCountryUserChosen || this.otpSent) {
      return
    }
    try {
      const [sabIso, geo] = await Promise.all([
        sabAPI.getCountryCode({ refresh: true }).catch(() => null),
        getIpGeoSnapshot({ refresh: true }).catch(() => null)
      ])
      console.log('sabIso', sabIso)
      console.log('geo', geo)
      const iso = resolveCountryIsoFromIpHints(sabIso, geo, this.$store.state.window.countryCode)
      console.log('iso', iso)
      this.$store.commit('window/SET_COUNTRY_CODE', iso)
      this.applyCountryFromIso(iso)
    } catch {
      this.applyCountryFromIso(this.$store.state.window.countryCode)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onCountryPickerKeydown)
    document.removeEventListener('click', this.onDocumentClickClosePicker)
  },
  methods: {
    applyCountryFromIso(iso) {
      const row = countryEntryForIpIso(iso)
      if (row) {
        this.selectedCountry = { ...row }
      }
    },
    applyApiError(err) {
      this.errorCode = err && err.code != null ? err.code : null
      this.errorDetail = messageFromApiError(err)
    },
    clearApiError() {
      this.errorCode = null
      this.errorDetail = null
    },
    /** Overrides formSubmit mixin so PIN step can show server error messages. */
    async submit() {
      this.clearApiError()
      if (!this.isValid()) {
        return
      }
      this.loading = true
      try {
        await this.verifyLogin()
      } catch (err) {
        this.applyApiError(err)
      }
      this.loading = false
    },
    flagEmoji,
    onCountryPickerKeydown(e) {
      if (e.key === 'Escape') {
        this.countryPickerOpen = false
      }
    },
    onDocumentClickClosePicker(e) {
      if (!this.countryPickerOpen || !this.$refs.countryWrap) {
        return
      }
      if (!this.$refs.countryWrap.contains(e.target)) {
        this.countryPickerOpen = false
      }
    },
    toggleCountryPicker() {
      this.countryPickerOpen = !this.countryPickerOpen
    },
    selectCountry(c) {
      this.phoneCountryUserChosen = true
      this.selectedCountry = { ...c }
      this.countryPickerOpen = false
      this.countrySearch = ''
    },
    normalizedDigits(value) {
      return String(value || '').replace(/\D/g, '')
    },
    onNationalNumberInput(e) {
      const el = e.target
      const digits = this.normalizedDigits(el.value)
      this.nationalNumber = digits
      this.$nextTick(() => {
        if (el && document.activeElement === el && el.value !== digits) {
          el.value = digits
        }
      })
    },
    onNationalNumberPaste(e) {
      e.preventDefault()
      const text = (e.clipboardData || window.clipboardData).getData('text') || ''
      this.nationalNumber = this.normalizedDigits(this.nationalNumber + text)
    },
    onNationalNumberKeydown(e) {
      const nav = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End'
      ]
      if (nav.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
        return
      }
      if (e.key.length === 1 && /\d/.test(e.key)) {
        return
      }
      e.preventDefault()
    },
    /** Numeric parts for v2 SAB phone session APIs. */
    apiPhoneParts() {
      const dial = String(this.selectedCountry.dial || '').replace(/\D/g, '')
      const national = this.normalizedDigits(this.nationalNumber)
      const countryCode = dial ? parseInt(dial, 10) : 0
      const phoneNumber = national ? parseInt(national, 10) : 0
      return { countryCode, phoneNumber }
    },
    async sendPin() {
      this.clearApiError()
      this.$v.nationalNumber.$touch()
      if (this.$v.nationalNumber.$invalid) {
        return
      }

      this.loading = true
      try {
        const parts = this.apiPhoneParts()
        const result = await sabAPI.sendPhonePincode(parts)
        if (result.sessionEstablished) {
          await authenticationService.finalizeSessionAfterExternalLogin()
          this.$emit('authenticated')
          return
        }
        this.otpSent = true
        this.pin = ''
        this.$nextTick(() => this.$v.$reset())
      } catch (err) {
        this.applyApiError(err)
      } finally {
        this.loading = false
      }
    },
    resetOtp() {
      this.otpSent = false
      this.pin = ''
      this.clearApiError()
      this.$v.$reset()
    },
    backToEmail() {
      this.$emit('changeAuthType', null, {}, true)
    },
    onSubmit() {
      if (!this.otpSent) {
        return
      }
      this.submit()
    },
    async verifyLogin() {
      const { countryCode, phoneNumber } = this.apiPhoneParts()
      await authenticationService.signInWithPhone({
        signInParameters: {
          authenticationPlatform: AuthenticationPlatform.PHONE,
          credentials: {
            pincode: this.normalizedDigits(this.pin),
            countryCode,
            phoneNumber
          }
        }
      })
      this.$emit('authenticated')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/scss/variables.scss';

.phone-login {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  .input-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding-top: 50px;

    .phone-field-wrap {
      position: relative;
      width: 100%;
      z-index: 5;
    }

    .phone-field {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      width: 100%;
      min-height: 3.2rem;
      border-radius: 8px;
      border: 1px solid #c5c9cb;
      background: #fff;
      overflow: hidden;
      transition: border-color 0.15s ease;
      box-sizing: border-box;

      &--error {
        border-color: $error-color;
      }

      &__prefix {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 6px;
        padding: 0 10px;
        border: none;
        background: transparent;
        cursor: pointer;
        font-family: Nunito, sans-serif;
        font-size: 1.05rem;
        font-weight: 600;
        color: #2b2f31;
        flex: 0 0 auto;
        max-width: 8.75rem;
        min-width: 0;

        &:focus {
          outline: none;
        }

        &:focus-visible {
          box-shadow: inset 0 0 0 2px rgba(162, 23, 171, 0.35);
        }
      }

      &__dial {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__caret {
        flex-shrink: 0;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 6px solid #2b2f31;
        margin-top: 2px;
      }

      &__divider {
        width: 1px;
        align-self: stretch;
        margin: 8px 0;
        background: #c5c9cb;
        flex-shrink: 0;
      }

      &__input-wrap {
        flex: 1 1 0;
        min-width: 0;
        display: flex;
        align-items: center;

        input {
          width: 100%;
          min-width: 0;
          border: none;
          padding: 12px 12px 12px 10px;
          font-family: Nunito, sans-serif;
          font-size: 1.1rem;
          color: #606568;
          background: transparent;
          outline: none;
          box-sizing: border-box;

          &::placeholder {
            color: #a8adb0;
          }

          &:focus {
            outline: none;
          }
        }
      }
    }

    .country-dropdown {
      position: absolute;
      left: 0;
      right: 0;
      top: calc(100% + 6px);
      z-index: 20;
      display: flex;
      flex-direction: column;
      max-height: min(250px, 55vh);
      background: #fff;
      border: 1px solid #c5c9cb;
      border-radius: 8px;
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.12);
      overflow: hidden;
      box-sizing: border-box;
    }

    .country-dropdown__search {
      flex-shrink: 0;
      width: 100%;
      padding: 10px 12px;
      font-family: Nunito, sans-serif;
      font-size: 1rem;
      border: none;
      border-bottom: 1px solid #e8eaeb;
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-bottom-color: #a217ab;
      }
    }

    .country-dropdown__list {
      list-style: none;
      margin: 0;
      padding: 0;
      overflow-y: auto;
      flex: 1;
      min-height: 0;
      -webkit-overflow-scrolling: touch;
    }

    .country-dropdown__row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-bottom: 1px solid #eef0f1;
      cursor: pointer;
      font-family: Nunito, sans-serif;
      font-size: 0.98rem;

      &:last-child {
        border-bottom: none;
      }

      &:hover,
      &:focus {
        background: #f7f7f7;
      }
    }

    .country-dropdown__flag {
      font-size: 1.35rem;
      line-height: 1;
      flex-shrink: 0;
    }

    .country-dropdown__name {
      flex: 1;
      min-width: 0;
      text-align: left;
      color: #2b2f31;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .country-dropdown__code {
      flex-shrink: 0;
      color: #606568;
      font-size: 0.9rem;
    }

    .otp-hint {
      margin: 0 0 1rem;
      font-size: 1.4rem;
      color: #606568;
    }

    > .btn {
      margin: 2rem 0 0;
    }

    .change-number,
    .back-email {
      display: block;
      margin-top: 1.5rem;
      text-align: center;
      font-size: 1.4rem;
      margin-left: auto;
      margin-right: auto;
      max-width: 22rem;
      line-height: 1.35;
    }

    .back-email {
      margin-top: 2rem;
      white-space: nowrap;

      @media (max-width: 360px) {
        white-space: normal;
        max-width: 100%;
        padding: 0 0.25rem;
      }
    }
  }
}
</style>
